/*
  <mh-reviews> — vrais avis OpenReviews dans le design Merci Handy (sections/mh-reviews.liquid).

  Utilise l'API publique appelée par le widget OpenReviews (augur.js), avec la même clé publique :
    GET https://api.openreviews.io/v1/augur/shop/reviews?tenant=…&offset=0&limit=…   (avis boutique + stats)
    GET https://api.openreviews.io/v1/augur/reviews?gtin=<product.id>&tenant=…        (avis d'un produit)
  Chaque avis : { author, body, rating, created_at, verified, product: { name } }. Le produit noté est
  affiché sous le texte (data-show-product) ; il est retrouvé dans la boutique par la recherche Shopify
  (/search/suggest.json, titre identique) pour sa vignette et son lien, sinon seul son nom est affiché.
  Fiche produit sans avis : avis de la boutique à la place (data-empty="shop"), ou section masquée (data-empty="hide").
  En cas d'échec (API indisponible, aucun avis), le widget officiel OpenReviews est affiché à la place.
*/
if (!customElements.get('mh-reviews')) {
  const API = 'https://api.openreviews.io/v1/augur';

  customElements.define('mh-reviews', class extends HTMLElement {
    connectedCallback() {
      if (this.loaded) return;
      this.loaded = true;
      this.lang = document.documentElement.lang || 'fr';
      this.load();
    }

    async load() {
      const { tenant, source, gtin } = this.dataset;
      const limit = Number(this.dataset.limit) || 12;
      const minRating = Number(this.dataset.minRating) || 1;

      const pick = (data) => data.items
        .filter((review) => (Number(review.rating) || 0) >= minRating && String(review.body || '').trim())
        .slice(0, limit);

      try {
        if (!tenant) throw new Error('config');
        let data = { items: [], total: 0, average: 0 };
        if (source !== 'product' || gtin) data = await this.fetchReviews(Math.min(50, limit * 3));
        let items = pick(data);
        // Produit sans avis : avis de la boutique à la place (réglage « Produit sans avis »), sinon section masquée
        if (!items.length && source === 'product') {
          if (this.dataset.empty === 'hide') return this.hideSection();
          data = await this.fetchReviews(Math.min(50, limit * 3), 'shop');
          items = pick(data);
          this.dataset.state = 'shop-fallback';
        }
        if (!items.length) throw new Error('empty');

        this.renderCards(items);
        this.renderSummary(data, items);
        this.removeAttribute('aria-busy');
        this.querySelector('mh-slider')?.update?.();
      } catch (error) {
        this.renderFallback();
      }
    }

    async fetchReviews(limit, source = this.dataset.source) {
      const { tenant, gtin } = this.dataset;
      const params = new URLSearchParams({ tenant, offset: '0', limit: String(limit) });
      let url = `${API}/shop/reviews`;
      if (source === 'product') {
        params.set('gtin', gtin);
        url = `${API}/reviews`;
      }
      const response = await fetch(`${url}?${params}`, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const json = await response.json();
      return {
        items: Array.isArray(json.items) ? json.items : [],
        total: Number(json.total_reviews) || 0,
        average: Number(json.avg_rating) || 0,
      };
    }

    renderCards(items) {
      const template = this.querySelector('template[data-mh-review-template]');
      const track = this.querySelector('[data-mh-slider-track]');
      if (!template || !track) return;

      track.replaceChildren(...items.map((review) => {
        const node = template.content.firstElementChild.cloneNode(true);
        const rating = Math.max(0, Math.min(5, Number(review.rating) || 0));
        const author = this.formatName(review.author);

        node.querySelector('[data-mh-stars]')?.style.setProperty('--mh-rating', rating);
        this.setText(node, '[data-review-rating]', this.formatNumber(rating));
        this.setText(node, '[data-review-body]', String(review.body || '').trim());
        this.setText(node, '[data-review-author]', author);

        const verified = node.querySelector('[data-review-verified]');
        if (verified) verified.hidden = !review.verified;

        this.renderProduct(node, review.product && review.product.name);

        const date = node.querySelector('[data-review-date]');
        if (date && review.created_at) {
          date.dateTime = review.created_at;
          date.textContent = this.relativeDate(review.created_at);
        }
        return node;
      }));
    }

    // Produit noté : nom nettoyé (« 🎁 … (100% off) », « (en cadeau) », « (2€) »), puis vignette et lien
    renderProduct(node, rawName) {
      const link = node.querySelector('[data-review-product]');
      if (!link) return;
      const name = this.cleanProductName(rawName);
      if (this.dataset.showProduct === 'false' || !name) return;
      this.setText(link, '[data-review-product-name]', name);
      link.hidden = false;
      this.findProduct(name).then((product) => {
        if (!product) return;
        link.href = product.url;
        const media = link.querySelector('[data-review-product-media]');
        if (media && product.image) {
          const img = document.createElement('img');
          const separator = product.image.includes('?') ? '&' : '?';
          img.src = `${product.image}${separator}width=96`;
          img.alt = '';
          img.width = 32;
          img.height = 32;
          img.loading = 'lazy';
          media.replaceChildren(img);
          media.hidden = false;
        }
      });
    }

    cleanProductName(name) {
      return String(name || '')
        .replace(/[\p{Extended_Pictographic}️]/gu, '')
        .replace(/\((?:[^)]*%[^)]*|en cadeau|offerte?s?|gift|free|\d+(?:[.,]\d+)?\s*€)\)/gi, '')
        .replace(/\s+/g, ' ')
        .trim();
    }

    // Une recherche par nom de produit, partagée entre les cartes et les sections
    findProduct(name) {
      const cache = (window.mhReviewProducts = window.mhReviewProducts || {});
      const key = name.toLowerCase();
      if (!cache[key]) {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const params = new URLSearchParams({ q: name, 'resources[type]': 'product', 'resources[limit]': '3' });
        cache[key] = fetch(`${root}search/suggest.json?${params}`, { headers: { Accept: 'application/json' } })
          .then((response) => (response.ok ? response.json() : null))
          .then((json) => {
            const results = (json && json.resources && json.resources.results && json.resources.results.products) || [];
            const match = results.find((product) => String(product.title || '').trim().toLowerCase() === key);
            if (!match) return null;
            let url = String(match.url || '').split('?')[0];
            // Modèle de fiche produit MH (réglage du thème, comme snippets/mh-collection-url.liquid)
            if (this.dataset.productView) url += `?view=${encodeURIComponent(this.dataset.productView)}`;
            return { url, image: match.featured_image?.url || match.image || '' };
          })
          .catch(() => null);
      }
      return cache[key];
    }

    renderSummary(data, items) {
      const summary = this.querySelector('[data-reviews-summary]');
      if (!summary || !data.total || !data.average) return;

      const average = Math.round(data.average * 10) / 10;
      summary.querySelector('[data-mh-stars]')?.style.setProperty('--mh-rating', average);
      this.setText(summary, '[data-reviews-average]', this.formatNumber(average));

      const label = summary.querySelector('[data-reviews-average-label]');
      if (label) label.textContent = label.dataset.template.replace('__RATING__', this.formatNumber(average));

      const count = new Intl.NumberFormat(this.lang).format(data.total);
      this.setText(summary, '[data-reviews-total]', (this.dataset.countTemplate || '(__COUNT__)').replace('__COUNT__', count));

      summary.hidden = false;
    }

    hideSection() {
      this.removeAttribute('aria-busy');
      (this.closest('.shopify-section') || this.closest('section') || this).hidden = true;
    }

    // Widget officiel OpenReviews si l'API ne répond pas comme prévu
    renderFallback() {
      const fallback = this.querySelector('[data-mh-reviews-fallback]');
      this.removeAttribute('aria-busy');
      this.dataset.state = 'fallback';
      if (!fallback || !this.dataset.tenant) return;

      const script = document.createElement('script');
      script.src = 'https://api.openreviews.io/augur.js';
      script.defer = true;
      script.dataset.tenant = this.dataset.tenant;
      script.dataset.variant = 'carousel';
      script.dataset.outOf = 'on';
      if (this.dataset.source === 'product' && this.dataset.gtin) script.dataset.gtin = this.dataset.gtin;
      else script.setAttribute('data-shop-reviews', '');
      fallback.replaceChildren(script);
      fallback.hidden = false;
    }

    setText(root, selector, text) {
      const element = root.querySelector(selector);
      if (element) element.textContent = text;
    }

    formatNumber(value) {
      return new Intl.NumberFormat(this.lang, { maximumFractionDigits: 1 }).format(value);
    }

    // « Héléna Therouin » → « Héléna T. » si le format court est choisi
    formatName(name) {
      const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
      if (!parts.length) return '';
      if (this.dataset.nameFormat !== 'short' || parts.length === 1) return parts.join(' ');
      return `${parts[0]} ${parts[parts.length - 1].charAt(0).toUpperCase()}.`;
    }

    // « Il y a 16 jours », « Il y a 2 mois »… dans la langue de la page
    relativeDate(iso) {
      const days = Math.round((new Date(iso).getTime() - Date.now()) / 86400000);
      if (Number.isNaN(days)) return '';
      const format = new Intl.RelativeTimeFormat(this.lang, { numeric: 'auto' });
      let text;
      if (Math.abs(days) < 30) text = format.format(days, 'day');
      else if (Math.abs(days) < 365) text = format.format(Math.round(days / 30), 'month');
      else text = format.format(Math.round(days / 365), 'year');
      return text.charAt(0).toUpperCase() + text.slice(1);
    }
  });
}
